/**
 * Web push registration
 *
 * Registers the FCM service worker, requests notification permission, gets the
 * device token with the VAPID key, and stores it on users/{uid}.fcmTokens so
 * the Cloud Functions sendPush helper can deliver booking/payment/message
 * notifications. Mirrors the mobile PushService.
 */

import { getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db, getMessagingIfSupported } from './firebase';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
let foregroundHooked = false;

/**
 * Call once the user is authenticated. Best-effort: never throws, and silently
 * no-ops when the browser doesn't support push or permission is denied.
 */
export async function registerWebPush(uid: string): Promise<void> {
  try {
    if (!VAPID_KEY || !('serviceWorker' in navigator) || !('Notification' in window)) {
      return;
    }

    const messaging = await getMessagingIfSupported();
    if (!messaging) return;

    // Only prompt if not already decided (avoids nagging on every load).
    if (Notification.permission === 'denied') return;
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return;
    }

    const registration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js'
    );

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return;

    await setDoc(
      doc(db, 'users', uid),
      { fcmTokens: arrayUnion(token), fcmUpdatedAt: serverTimestamp() },
      { merge: true }
    );

    // Foreground messages: FCM doesn't show a notification while the tab is
    // focused, so surface a toast instead. Hook once.
    if (!foregroundHooked) {
      foregroundHooked = true;
      onMessage(messaging, (payload) => {
        const title = payload.notification?.title;
        const body = payload.notification?.body;
        if (title || body) {
          toast(`${title || 'DARAK'}${body ? `: ${body}` : ''}`);
        }
      });
    }
  } catch (err) {
    console.error('Web push registration failed:', err);
  }
}
